import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

const Hero = () => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (!terminalRef.current) return;
    
    const typeText = async (element: HTMLElement, text: string, delay = 60) => {
      for (let i = 0; i < text.length; i++) {
        element.textContent += text[i];
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    };
    
    const runTerminalAnimation = async () => {
      const terminalText = terminalRef.current?.querySelector('.terminal-text');
      if (!terminalText) return;
      
      terminalText.textContent = '';
      await typeText(terminalText as HTMLElement, '> Hello! I\'m Siva, your super agent.');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      terminalText.textContent = '';
      await typeText(terminalText as HTMLElement, '> I can help with workflows, research, and more.');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      terminalText.textContent = '';
      await typeText(terminalText as HTMLElement, '> Join the waitlist to be one of the first to try me out!');
    };
    
    const timeout = setTimeout(runTerminalAnimation, 800);
    return () => clearTimeout(timeout);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([{ email }]);

      if (error) throw error;

      toast.success('Thanks for joining the waitlist! We\'ll be in touch soon.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section className="pt-32 pb-24 px-4 md:pt-40 md:pb-32">
      <Toaster position="top-center" />
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">
              Meet Siva, Your Super Intelligent Virtual Assistant
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed">
              Siva is the intelligent agent that helps you accomplish more on your computer. 
              From automating workflows to conducting research, Siva handles the repetitive 
              tasks so you can focus on what matters.
            </p>
            
            <form onSubmit={handleSubmit} className="max-w-md mx-auto lg:mx-0">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-white rounded-lg font-medium hover:shadow-lg hover:from-teal-500 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                </button>
              </div>
            </form>
          </div>
          
          <div className="relative" ref={terminalRef}>
            <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-teal-400 to-purple-500 opacity-70 blur-sm animate-pulse"></div>
            <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-xl overflow-hidden">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-700 mb-4">
                <div className="h-3 w-3 rounded-full bg-red-500"></div>
                <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
                <div className="ml-2 text-sm text-slate-400">Siva Terminal</div>
              </div>
              
              <div className="flex items-start gap-3">
                <Terminal className="h-6 w-6 text-teal-400 mt-1 flex-shrink-0" />
                <div className="terminal-text text-slate-300 min-h-[90px] flex items-center font-mono"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;